import { motion } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import { ScrollReveal, StaggerParent, StaggerItem } from "@/components/shared/ScrollReveal";
import { useLang } from "@/i18n/LanguageContext";

const THUMBS = [
  // Modo News — grattacielo Milano di notte (custom)
  `${process.env.PUBLIC_URL}/images/modo/news.jpg`,
  // Modo Motori — auto sportiva gialla (custom)
  `${process.env.PUBLIC_URL}/images/modo/motori.jpg`,
  // Modo Food — spaghetti (custom)
  `${process.env.PUBLIC_URL}/images/modo/food.jpg`,
  // Modo Economia — monete e grafico (custom)
  `${process.env.PUBLIC_URL}/images/modo/economia.jpg`,
];

/**
 * Modo — bold XL design with orange signature line.
 */
export default function VideoStrip() {
  const { t } = useLang();
  return (
    <section
      id="video"
      data-testid="video-strip-section"
      className="relative bg-black text-white py-24 md:py-40 border-t border-white/10 overflow-hidden scroll-mt-20"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        {/* Header row */}
        <ScrollReveal>
          <div className="flex items-center gap-3 mb-8">
            <motion.span
              animate={{ opacity: [1, 0.35, 1] }}
              transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
              className="w-2 h-2 rounded-full bg-[var(--dp-accent)]"
            />
            <p className="font-mono-eyebrow text-[var(--dp-accent)]">{t.videoStrip.eyebrow}</p>
          </div>
        </ScrollReveal>

        {/* Massive MODO title */}
        <ScrollReveal delay={0.1}>
          <h2 className="font-display text-[28vw] md:text-[24vw] lg:text-[280px] leading-[0.82] tracking-[-0.06em] mb-8 md:mb-10 flex items-end">
            <span>Modo</span>
            <span className="text-[var(--dp-accent)]">.</span>
          </h2>
        </ScrollReveal>

        {/* Signature orange line */}
        <ScrollReveal delay={0.2}>
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, margin: "-8% 0px" }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="h-[3px] bg-[var(--dp-accent)] origin-left mb-8 md:mb-12"
          />
        </ScrollReveal>

        {/* Description row */}
        <ScrollReveal delay={0.25}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-16 md:mb-24">
            <p className="md:col-span-6 md:col-start-1 text-neutral-300 text-xl md:text-2xl leading-snug tracking-tight max-w-2xl">
              {t.videoStrip.body}
            </p>
            <p className="md:col-span-4 md:col-start-9 font-mono-eyebrow text-neutral-500 self-end">
              04 canali · 2 live · 2 in arrivo
            </p>
          </div>
        </ScrollReveal>

        {/* 4 XL channel cards */}
        <StaggerParent className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          {t.video.items.map((v, i) => {
            const Wrapper = ({ children }) =>
              v.comingSoon ? (
                <div
                  data-testid={`modo-card-${i}`}
                  className="group relative overflow-hidden aspect-[4/3] md:aspect-[16/10] bg-[#0a0a0a] border border-white/10 block"
                >
                  {children}
                </div>
              ) : (
                <a
                  href={v.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`modo-card-${i}`}
                  aria-label={`${v.name} — YouTube`}
                  className="group relative overflow-hidden aspect-[4/3] md:aspect-[16/10] bg-[#0a0a0a] border border-white/10 hover:border-[var(--dp-accent)] transition-colors block"
                >
                  {children}
                </a>
              );

            return (
              <StaggerItem key={v.name}>
                <Wrapper>
                  <img
                    src={THUMBS[i % THUMBS.length]}
                    alt=""
                    className={`w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105 ${
                      v.comingSoon ? "grayscale opacity-40" : "opacity-70 group-hover:opacity-90"
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />

                  {/* Orange corner accent */}
                  <div className="absolute top-0 left-0 w-16 h-[3px] bg-[var(--dp-accent)]" />
                  <div className="absolute top-0 left-0 h-16 w-[3px] bg-[var(--dp-accent)]" />

                  {/* Number */}
                  <span className="absolute top-6 md:top-8 right-6 md:right-8 font-mono-eyebrow text-neutral-400">
                    {String(i + 1).padStart(2, "0")} / 04
                  </span>

                  {/* Content bottom */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                    <p className="font-mono-eyebrow text-neutral-400 mb-3">{v.cat}</p>
                    <h3 className="font-display text-4xl md:text-6xl lg:text-7xl leading-none tracking-tight text-white mb-5">
                      <span className="inline-block group-hover:translate-x-2 transition-transform duration-500">
                        {v.name}
                      </span>
                    </h3>
                    <div className="flex items-center justify-between border-t border-white/20 pt-4">
                      {v.comingSoon ? (
                        <span className="font-mono-eyebrow text-[var(--dp-accent)]">
                          ● {t.videoStrip.soon}
                        </span>
                      ) : (
                        <span className="font-mono-eyebrow text-white flex items-center gap-2">
                          <Play size={12} className="text-[var(--dp-accent)]" />
                          Guarda su YouTube
                        </span>
                      )}
                      {!v.comingSoon && (
                        <span className="w-11 h-11 rounded-full bg-[var(--dp-accent)] text-white flex items-center justify-center group-hover:rotate-45 transition-transform duration-500">
                          <ArrowUpRight size={16} />
                        </span>
                      )}
                    </div>
                  </div>
                </Wrapper>
              </StaggerItem>
            );
          })}
        </StaggerParent>
      </div>
    </section>
  );
}
